import React from 'react';
import { lighten, styled } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker as MuiTimePicker, TimePickerProps as MuiTimePickerProps } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useTranslation } from '@askporter/config-provider';

dayjs.extend(customParseFormat);
export interface TimePickerProps extends Omit<MuiTimePickerProps, 'onChange' | 'value' | 'maxTime' | 'minTime'> {
  label: string;
  value: string | null;
  maxTime?: string | null;
  minTime?: string | null;
  onChange: (value: string | null) => void;
}

const StyledTimePicker = styled(MuiTimePicker)(({ theme }) => ({
  '.MuiPickersInputBase-root': {
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#414651',
    WebkitTextFillColor: '#414651',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px',
    '&:focus-within': {
      boxShadow: `0 0 0 1px ${lighten(theme.palette.primary.main, 0.4)}, 0 0 0 4px ${lighten(
        theme.palette.primary.main,
        0.8,
      )}`,
      '&.Mui-error': {
        boxShadow: `0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(240, 68, 56, 0.24)`,
      },
      backgroundColor: '#fff',
      outline: 'none',
      borderRadius: '8px !important',
    },
    '& .MuiPickersSectionList-root': {
      padding: '12px 0',
      marginLeft: '6px',
    },
    flexDirection: 'row-reverse',
    '& .MuiInputAdornment-root': {
      marginLeft: 0,
      '& .MuiIconButton-root': {
        marginLeft: '-8px',
      },
    },
  },
}));

const parseHHmm = (t?: string | null) => (t ? dayjs(t, 'HH:mm', true) : null);

export const TimePicker: React.FC<TimePickerProps> = ({ label, value, onChange, maxTime, minTime, ...props }) => {
  const { i18n } = useTranslation();
  const adapterLocale = i18n.language ? i18n.language.split('-')[0] : 'en';

  const [val, setVal] = React.useState<Dayjs | null>(parseHHmm(value));

  // keep internal state in sync with prop changes
  React.useEffect(() => {
    setVal(parseHHmm(value));
  }, [value]);

  const handleChange = (newValue: Dayjs | null) => {
    setVal(newValue);
    onChange(newValue ? newValue.format('HH:mm') : null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      <StyledTimePicker
        value={val}
        onChange={handleChange}
        slotProps={{
          dialog: { 'aria-label': label },
          popper: { 'aria-label': label },
          textField: {
            sx: { marginTop: 0, marginBottom: 0 },
          },
        }}
        maxTime={parseHHmm(maxTime) ?? undefined}
        minTime={parseHHmm(minTime) ?? undefined}
        ampm={false}
        format="HH:mm"
        {...props}
      />
    </LocalizationProvider>
  );
};
